import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: A new Snowflake Organization section and organization-level Usage Groups\ndate: 2024-03-28T12:00:00.000Z\nauthorNames: [niall]\ndescription: We've released a new, dedicated Snowflake Organization section in SELECT and added support for organization-level Usage Groups.\npr: https://github.com/get-select/select/pull/2079\n---\n\nAs a follow-up to the [Snowflake Organization Cost Explorer](/docs/snowflake-organization#organization-cost-explorer) we [released last month](/changelog/2024-02-17-organization-dashboard), we're excited to announce a brand new Snowflake Organization section in SELECT.\n\n## Snowflake Organization Section\n\nUsing the updated account switcher, you can now switch between seeing details for a single Snowflake account view to all accounts in your Snowflake organization.\n\n{% blog-post-image imageName=\"snowflakeAccountSwitcher\" alt=\"SELECT Snowflake account switcher\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20 md:w-4/5\"/%}\n\nIn the new Snowflake Organization section of SELECT, you'll see a brand new dashboard containing:\n\n1. A snapshot of your annual Snowflake organization spend\n2. A breakdown of this spend by account, service, warehouse and [Usage Group](/docs/usage-groups)\n3. A variety of filter options to help you further explore and analyze your usage patterns\n\n{% video googleCloudKey=\"docs/organization_dashboard_overview\" /%}\n\nIn the sidebar, you'll see a different set of options than the usual \"single\" Snowflake Account view in SELECT:\n\n1. The [Organization Cost Explorer](/docs/snowflake-organization#organization-cost-explorer)\n2. Organization level [Usage Groups](/docs/usage-groups) (NEW!)\n3. The [Snowflake Contract Utilization](/docs/snowflake-organization#snowflake-contract-utilization) section\n4. Organization level [Monitors](/docs/monitors)\n5. Organization level settings\n\n## New Organization Level Usage Groups\n\nAs part of this change, we extended our [Usage Groups](/docs/usage-groups) feature to allow users to create Usage Groups across all accounts within your Snowflake Organization.\n\nWith organization-level Usage Groups, you can create groups using the following options:\n\n* Snowflake account\n* Service (Compute, Storage, Data Transfer, Serverless Tasks, etc.)\n* Snowflake Region (AWS_US_EAST_1, GCP_US_EAST4, AZURE_WESTEUROPE, etc.)\n* Warehouse Name\n\n\n{% blog-post-image imageName=\"organizationUsageGroups\" alt=\"SELECT organization cost allocation with Usage Groups\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20\"/%}";
const filepath = "/changelog/2024-03-28-new-snowflake-organization-section.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
